import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Copy, Download, Loader2, CheckCircle2, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { auth, ApiError } from '@/api/client';
import { AuthShell, HipaaBadge, AuthErrorBanner } from '@/components/auth/AuthShell';

/**
 * MFA enrolment — DREAM-2 AC14.
 *
 * Step 1: QR + manual-entry secret + 8 recovery codes.
 * Step 2: capture first TOTP code ? server confirms enrolment.
 */

interface EnrolData {
  qr_data_uri: string;
  secret: string;
  recovery_codes: string[];
}

export function MfaEnrolPage() {
  const { pendingChallenge, setChallenge, cancelChallenge } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase]         = useState<'loading' | 'show' | 'verify'>('loading');
  const [data, setData]           = useState<EnrolData | null>(null);
  const [error, setError]         = useState<string | null>(null);
  const [saved, setSaved]         = useState(false);
  const [code, setCode]           = useState('');
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied]       = useState(false);

  useEffect(() => {
    if (!pendingChallenge) return;
    if (pendingChallenge.step !== 'mfa_enrol_required') return;
    let cancelled = false;
    auth
      .mfaEnrolStart(pendingChallenge.token)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setPhase('show');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.code === 'UNAUTHORIZED' || err.code === 'FORBIDDEN')) {
          setError('Your session has expired. Please sign in again.');
        } else {
          setError('Could not start MFA setup. Please sign in again.');
        }
      });
    return () => { cancelled = true; };
  }, [pendingChallenge]);

  if (!pendingChallenge) return <Navigate to="/login" replace />;
  if (pendingChallenge.step !== 'mfa_enrol_required') return <Navigate to="/auth/mfa/challenge" replace />;

  async function copyCodes() {
    if (!data) return;
    const text = data.recovery_codes.join('\n');
    let success = false;
    if (navigator.clipboard) {
      try { await navigator.clipboard.writeText(text); success = true; } catch { /* fall through */ }
    }
    if (!success) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { success = document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function downloadCodes() {
    if (!data) return;
    const text = [
      `DreamTrials — recovery codes`,
      `Account: ${pendingChallenge?.email ?? ''}`,
      `Generated: ${new Date().toISOString()}`,
      ``,
      `Each code is single-use. Store these somewhere safe (a password manager is ideal).`,
      ``,
      data.recovery_codes.map((c, i) => `${i + 1}. ${c}`).join('\n'),
      ``,
    ].join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `dreamtrials-recovery-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    if (!pendingChallenge) return;
    setError(null);
    setVerifying(true);
    try {
      const r = await auth.mfaEnrolVerify(pendingChallenge.token, code.trim());
      setChallenge({ ...pendingChallenge, token: r.challenge_token, step: 'mfa_totp' });
      navigate('/auth/mfa/challenge');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'RATE_LIMITED') {
          setError('Too many attempts. Please wait before trying again.');
        } else if (err.code === 'VALIDATION_ERROR') {
          setError('Please enter the 6-digit code shown in your authenticator app.');
        } else {
          setError('Incorrect code. Please check your authenticator app and try again.');
        }
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  }

  const stepLabel = phase === 'verify' ? 'Step 2 of 2' : 'Step 1 of 2';
  const heading   = phase === 'verify' ? 'Confirm your authenticator' : 'Set up authenticator';

  return (
    <AuthShell
      title={<>Set up multi-factor<br/>authentication</>}
      lede="Protect your account with time-based one-time passwords (TOTP)."
    >
      <p className="eyebrow mb-2">{stepLabel}</p>
      <h1 className="text-[26px] font-extrabold text-text-1 mb-1.5 tracking-tight">{heading}</h1>
      <p className="text-[12.5px] text-text-3 mb-6 leading-relaxed">
        {phase === 'verify'
          ? 'Enter the 6-digit code now shown in your authenticator app to confirm setup.'
          : 'Follow the steps below to link your authenticator app.'}
      </p>

      <div className="relative bg-white border border-border rounded-sm p-7 pb-6 shadow-card">
        <HipaaBadge />

        {phase === 'loading' && (
          <div className="flex items-center justify-center gap-2.5 py-10 text-text-3 text-[13px]">
            <Loader2 size={16} className="animate-spin"/>
            Preparing your enrolment
          </div>
        )}

        {phase === 'show' && data && (
          <>
            <p className="eyebrow mb-3">1. Scan QR code</p>
            <p className="text-[12px] text-text-3 mb-4 leading-relaxed">
              Use Microsoft Authenticator, Google Authenticator, Authy, or any TOTP app you trust.
            </p>

            <div className="flex gap-4 items-start mb-6">
              <img
                src={data.qr_data_uri}
                alt="MFA QR code"
                className="w-36 h-36 rounded-sm border border-border bg-white p-2 shrink-0"
              />
              <div className="flex-1">
                <p className="text-[11.5px] text-text-3 mb-2 leading-relaxed">
                  Can't scan? Enter this secret manually in your app:
                </p>
                <code className="block break-all bg-soft border border-border-soft rounded-sm px-2.5 py-2 font-mono text-[11px] text-text-1 leading-relaxed">
                  {data.secret}
                </code>
              </div>
            </div>

            <div className="border-t border-border-soft pt-5 mb-3">
              <p className="eyebrow mb-2">2. Save recovery codes</p>
              <p className="text-[12px] text-text-3 mb-3.5 leading-relaxed">
                Each code works once if you lose your authenticator. Store them safely — you won't see them again.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 bg-soft border border-border-soft rounded-sm p-3 mb-3">
                {data.recovery_codes.map((c, i) => (
                  <code key={i} className="bg-band rounded-sm px-1 py-1.5 font-mono text-[10.5px] text-center text-text-1 tracking-wide">
                    {c}
                  </code>
                ))}
              </div>

              <div className="flex gap-2 mb-4">
                <button
                  type="button" onClick={copyCodes}
                  className="inline-flex items-center justify-center gap-1.5 h-[38px] px-3.5 rounded-sm bg-white border border-border text-navy text-[12px] font-bold hover:bg-soft hover:border-navy transition-colors"
                >
                  {copied ? <><CheckCircle2 size={13}/> Copied!</> : <><Copy size={13}/> Copy all</>}
                </button>
                <button
                  type="button" onClick={downloadCodes}
                  className="inline-flex items-center justify-center gap-1.5 h-[38px] px-3.5 rounded-sm bg-white border border-border text-navy text-[12px] font-bold hover:bg-soft hover:border-navy transition-colors"
                >
                  <Download size={13}/> Download .txt
                </button>
              </div>

              <label className="flex items-start gap-2.5 text-[12.5px] text-text-2 mb-5 cursor-pointer">
                <input
                  type="checkbox" checked={saved}
                  onChange={e => setSaved(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 shrink-0 accent-navy"
                />
                <span>I have saved my recovery codes somewhere safe.</span>
              </label>

              <button
                type="button" disabled={!saved}
                onClick={() => setPhase('verify')}
                className="w-full h-12 bg-navy hover:bg-navy-600 active:bg-navy-700 text-white font-bold text-[13.5px] tracking-wide rounded-sm flex items-center justify-center gap-1.5 transition-colors shadow-card disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight size={14} className="opacity-75" />
              </button>
            </div>
          </>
        )}

        {phase === 'verify' && (
          <form onSubmit={handleVerify} noValidate>
            <p className="eyebrow mb-3">3. Enter authenticator code</p>
            <p className="text-[12px] text-text-3 mb-4 leading-relaxed">
              Type the current 6-digit code shown in your authenticator app to complete setup.
            </p>

            <div className="mb-[18px]">
              <input
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="w-full h-16 rounded-sm border border-border bg-white font-mono font-bold text-[28px] tracking-[0.35em] text-center text-text-1 outline-none px-3.5 transition-colors focus:border-navy"
                autoFocus
                required
              />
            </div>

            {error && <div className="mb-3.5"><AuthErrorBanner message={error} /></div>}

            <button
              type="submit" disabled={code.length !== 6 || verifying}
              className="w-full h-12 bg-navy hover:bg-navy-600 active:bg-navy-700 text-white font-bold text-[13.5px] tracking-wide rounded-sm flex items-center justify-center gap-1.5 transition-colors shadow-card disabled:opacity-65 disabled:cursor-not-allowed"
            >
              {verifying
                ? <><Loader2 size={14} className="animate-spin"/> Verifying</>
                : <><CheckCircle2 size={15}/> Confirm and continue</>}
            </button>
          </form>
        )}

        {error && phase === 'show' && (
          <div className="mt-4"><AuthErrorBanner message={error} /></div>
        )}
      </div>

      <div className="mt-5 text-center">
        <Link
          to="/login"
          onClick={cancelChallenge}
          className="inline-flex items-center gap-1.5 text-[12px] font-bold text-navy hover:underline"
        >
          <ArrowLeft size={13}/> Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}
